// src/pages/Delivery.jsx

import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { BiEdit, BiSearch } from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import PageImageSelector from "../components/ui/PageImageSelector";
import LoadingSpinner from "../components/LoadingSpinner";
import heroImg from "../assets/car-delivery/car-delivery-hero-bg.jpg";
import { HiMiniCheckBadge } from "react-icons/hi2";
import guaranteeIcon from "../assets/findcar/guarantee_icon.png";
import ratingIcon from "../assets/findcar/rating_icon.png";
import del1 from "../assets/car-delivery/Car delivery.png";
import mg1 from "../assets/car-delivery/Mask group1.png";
import mg2 from "../assets/car-delivery/Maskgroup2.png";
import mg3 from "../assets/car-delivery/Maskgroup 3.png";
import bgImage from "../assets/car-delivery/bgimage.png";

export default function Delivery() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("super-admin");
  const navigate = useNavigate();

  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSection, setEditingSection] = useState(null);

  const heroFileInputRef = useRef(null);
  const behindScenesFileInputRef = useRef(null);
  const deliveryDayFileInputRef = useRef(null);
  const preferCollectionFileInputRef = useRef(null);

  useEffect(() => {
    api
      .get("/pages/delivery")
      .then((res) => {
        setPageContent(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Cannot load page content");
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!pageContent) return <p>No content</p>;

  async function saveContent(updatedContent) {
    try {
      await api.put("/pages/delivery", { content: updatedContent });
      setPageContent(updatedContent);
      toast.success("Saved successfully");
      setEditingSection(null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    }
  }

  async function uploadPageImage(file, onUrlReceived) {
    const form = new FormData();
    form.append("pageKey", "delivery");
    form.append("images", file);

    try {
      const res = await api.post("/page-images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newImg = res.data[0];
      onUrlReceived(newImg.url);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Image upload failed");
    }
  }

  const hero = pageContent.hero || { title: "", subtitle: "", imageUrl: "" };
  const whereDeliverSection = pageContent.sections.find(
    (s) => s.key === "where-do-we-deliver"
  ) || {
    key: "where-do-we-deliver",
    heading: "Where do we deliver?",
    paragraphs: [],
  };
  const behindScenesSection = pageContent.sections.find(
    (s) => s.key === "behind-the-scenes"
  ) || {
    key: "behind-the-scenes",
    heading: "Behind the scenes",
    paragraphs: [],
    imageUrl: "",
  };
  const deliveryDaySection = pageContent.sections.find(
    (s) => s.key === "delivery-day"
  ) || {
    key: "delivery-day",
    heading: "The delivery day",
    paragraphs: [],
    imageUrl: "",
  };
  const preferCollectionSection = pageContent.sections.find(
    (s) => s.key === "prefer-collection"
  ) || {
    key: "prefer-collection",
    heading: "Prefer collection?",
    subheading: "",
    button: { text: "", href: "" },
    imageUrl: "",
  };
  const readyOpenRoadSection = pageContent.sections.find(
    (s) => s.key === "ready-open-road"
  ) || {
    key: "ready-open-road",
    preamble: "",
    heading: "Ready to hit the open road?",
    button: { text: "", href: "" },
  };

  return (
    <div className="w-full">
      {/* ─── HERO SECTION ────────────────────────────────────────────────────── */}
      <section className="relative w-full h-[70vh] md:h-[90vh] flex items-end text-white overflow-hidden">
        <img
          src={heroImg || hero.imageUrl}
          alt="Hero background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(rgba(31, 41, 84, 0.5) 10%, rgba(239, 81, 75, 0.6) 77%)",
          }}
        />
        <div className="relative z-20 px-4 md:px-20 py-8 md:py-20 mb-20">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            {"Welcome to ZORQK"}
          </h1>
          <p className="text-2xl md:text-5xl font-bold">
            {"The future of motoring platforms"}
          </p>
        </div>
      </section>

      {/* Delivery By Specialist Section */}
      <section className="relative -mt-20 bg-[#1c2553] text-white py-10 rounded-t-3xl md:rounded-t-[50px] z-30">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 px-4 md:px-0">
          <div className="flex flex-col justify-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-accent font-bold mb-4 md:mb-6">
              Home Delivery
            </h2>
            <p className="text-base md:text-lg lg:text-xl leading-relaxed text-white/90 max-w-3xl mb-6">
              Your car delivered, when you’re free. Just choose a day and time
              that suits you, relax, and we’ll do the rest.
            </p>
            <div className="flex flex-col gap-6 md:gap-10 pt-6">
              <div className="flex gap-4 md:gap-5 items-center">
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow text-white">
                  <img
                    src={ratingIcon}
                    alt="5 star reviews"
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <p className="text-base md:text-lg lg:text-xl font-bold">
                  5* Reviews
                </p>
              </div>
              <div className="flex gap-4 md:gap-5 items-center">
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow text-white">
                  <img
                    src={guaranteeIcon}
                    alt="14 day guarantee"
                    className="w-6 h-6 object-cover"
                  />
                </div>
                <p className="text-base md:text-lg lg:text-xl font-bold">
                  Satisfaction Guaranteed
                </p>
              </div>
            </div>
          </div>
          <div>
            <img
              src={del1}
              alt="Home delivery"
              className="w-full h-[40vh] md:h-[72vh] rounded-3xl object-cover"
            />
          </div>
        </div>
      </section>

      {/* Where do we deliver? */}
      <section className="max-w-6xl mx-auto flex flex-col gap-12 md:gap-20 items-center py-10">
        <div className="flex flex-col items-center gap-6 md:gap-10 px-4 sm:px-20 text-center">
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-accent font-bold">
            Where do we deliver?
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-primary font-bold">
            We offer home delivery to mainland Great Britain and the Isle of
            Wight postcodes. Pick a delivery slot, Monday to Saturday, and we’ll
            deliver your car to your door.
          </p>
          <p className="text-sm md:text-base lg:text-lg text-primary">
            We don’t deliver to Northern Ireland, the Scottish Islands, the
            Isles of Scilly, the Isle of Man, Jersey, Guernsey and the other
            Channel Islands.
          </p>
          <p className="text-sm md:text-base lg:text-lg text-primary">
            We also reserve the right not to deliver to certain addresses due to
            vehicle access limitations.
          </p>
          {/* <button className="w-fit px-6 sm:px-20 py-2 border border-accent text-accent font-bold rounded-full hover:bg-accent hover:text-white transition">
            Find out more
          </button> */}
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-4 sm:gap-20 w-full h-[30vh] sm:h-[40vh] max-w-4xl mx-auto px-4 sm:px-4">
          <div className="flex-1 h-20 sm:h-full">
            <img
              className="w-full h-full object-cover rounded"
              src={mg1}
              alt="mask group 1"
            />
          </div>
          <div className="flex-1 h-20 sm:h-full">
            <img
              className="w-full h-full object-cover rounded"
              src={mg2}
              alt="mask group 2"
            />
          </div>
          <div className="flex-1 h-20 sm:h-full">
            <img
              className="w-full h-full object-cover rounded"
              src={mg3}
              alt="mask group 3"
            />
          </div>
        </div>
      </section>

      {/* “Behind the scenes” SECTION */}
      <section className="bg-accent py-10 relative">
        {isAdmin && editingSection !== "behind-the-scenes" && (
          <BiEdit
            onClick={() => setEditingSection("behind-the-scenes")}
            className="absolute top-4 right-4 text-xl text-white cursor-pointer hover:text-accent"
            title="Edit Behind the scenes"
          />
        )}
        <div className="max-w-6xl mx-auto px-4 sm:px-0">
          {editingSection === "behind-the-scenes" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
              <div className="flex justify-center mb-6 md:mb-0">
                <img
                  src={behindScenesSection.imageUrl}
                  alt={behindScenesSection.heading}
                  className="w-full rounded-2xl object-contain"
                />
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
                  value={behindScenesSection.heading}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "behind-the-scenes"
                          ? { ...sec, heading: e.target.value }
                          : sec
                      ),
                    }))
                  }
                  placeholder="Section Heading"
                />
                {behindScenesSection.paragraphs.map((para, idx) => (
                  <textarea
                    key={idx}
                    className="text-primary w-full mb-2 px-3 py-2 rounded"
                    rows={3}
                    value={behindScenesSection.paragraphs[idx]}
                    onChange={(e) => {
                      const newParas = [...behindScenesSection.paragraphs];
                      newParas[idx] = e.target.value;
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "behind-the-scenes"
                            ? { ...sec, paragraphs: newParas }
                            : sec
                        ),
                      }));
                    }}
                    placeholder={`Paragraph ${idx + 1}`}
                  />
                ))}

                <div className="mb-2">
                  <label className="block mb-1 text-sm font-medium text-primary">
                    Section Image
                  </label>
                  <PageImageSelector
                    pageKey="delivery"
                    value={behindScenesSection.imageUrl}
                    onChange={(newUrl) => {
                      if (newUrl === "__upload_new__") {
                        behindScenesFileInputRef.current.click();
                      } else {
                        setPageContent((prev) => ({
                          ...prev,
                          sections: prev.sections.map((sec) =>
                            sec.key === "behind-the-scenes"
                              ? { ...sec, imageUrl: newUrl }
                              : sec
                          ),
                        }));
                      }
                    }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref={behindScenesFileInputRef}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      uploadPageImage(file, (url) => {
                        setPageContent((prev) => ({
                          ...prev,
                          sections: prev.sections.map((sec) =>
                            sec.key === "behind-the-scenes"
                              ? { ...sec, imageUrl: url }
                              : sec
                          ),
                        }));
                      });
                      e.target.value = null;
                    }}
                  />
                </div>

                <div className="flex space-x-2">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={() => saveContent({ ...pageContent })}
                  >
                    Save
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded"
                    onClick={() => setEditingSection(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-[55%_40%] gap-8 md:gap-16 items-center px-4 md:px-10">
              <div className="flex justify-center">
                <img
                  src={behindScenesSection.imageUrl}
                  alt={behindScenesSection.heading}
                  className="w-full rounded-2xl object-contain"
                />
              </div>
              <div className="space-y-5">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white font-bold mb-6">
                  {behindScenesSection.heading}
                </h2>
                <p className="text-sm md:text-base lg:text-lg font-bold">
                  When you click ‘buy’ our experts kick into gear.
                </p>
                {behindScenesSection.paragraphs.map((paragraph, idx) => (
                  <p
                    key={idx}
                    className="text-sm md:text-base lg:text-lg text-white pb-6 md:pb-12"
                  >
                    {paragraph}
                  </p>
                ))}
                <button
                  onClick={() => navigate("/used-cars")}
                  className="w-full px-6 py-2 border-2 border-white text-white font-bold rounded-full hover:bg-accent transition"
                >
                  Find your dream car
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* “The delivery day” SECTION */}
      <section className="bg-white py-10 relative px-4">
        {isAdmin && editingSection !== "delivery-day" && (
          <BiEdit
            onClick={() => setEditingSection("delivery-day")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit The delivery day"
          />
        )}
        <div className="max-w-5xl mx-auto px-4 sm:px-0">
          {editingSection === "delivery-day" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center">
              <div className="space-y-4">
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
                  value={deliveryDaySection.heading}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "delivery-day"
                          ? { ...sec, heading: e.target.value }
                          : sec
                      ),
                    }))
                  }
                  placeholder="Section Heading"
                />
                {deliveryDaySection.paragraphs.map((para, idx) => (
                  <textarea
                    key={idx}
                    className="text-primary w-full mb-2 px-3 py-2 rounded"
                    rows={3}
                    value={deliveryDaySection.paragraphs[idx]}
                    onChange={(e) => {
                      const newParas = [...deliveryDaySection.paragraphs];
                      newParas[idx] = e.target.value;
                      setPageContent((prev) => ({
                        ...prev,
                        sections: prev.sections.map((sec) =>
                          sec.key === "delivery-day"
                            ? { ...sec, paragraphs: newParas }
                            : sec
                        ),
                      }));
                    }}
                    placeholder={`Paragraph ${idx + 1}`}
                  />
                ))}

                <div className="mb-2">
                  <label className="block mb-1 text-sm font-medium text-primary">
                    Section Image
                  </label>
                  <PageImageSelector
                    pageKey="delivery"
                    value={deliveryDaySection.imageUrl}
                    onChange={(newUrl) => {
                      if (newUrl === "__upload_new__") {
                        deliveryDayFileInputRef.current.click();
                      } else {
                        setPageContent((prev) => ({
                          ...prev,
                          sections: prev.sections.map((sec) =>
                            sec.key === "delivery-day"
                              ? { ...sec, imageUrl: newUrl }
                              : sec
                          ),
                        }));
                      }
                    }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref={deliveryDayFileInputRef}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      uploadPageImage(file, (url) => {
                        setPageContent((prev) => ({
                          ...prev,
                          sections: prev.sections.map((sec) =>
                            sec.key === "delivery-day"
                              ? { ...sec, imageUrl: url }
                              : sec
                          ),
                        }));
                      });
                      e.target.value = null;
                    }}
                  />
                </div>

                <div className="flex space-x-2">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={() => saveContent({ ...pageContent })}
                  >
                    Save
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded"
                    onClick={() => setEditingSection(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <div>
                <img
                  src={deliveryDaySection.imageUrl}
                  alt={deliveryDaySection.heading}
                  className="w-full rounded-2xl object-cover h-[300px] md:h-[500px]"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-20 items-center">
                <div className="space-y-6 md:space-y-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-brandAccent font-bold">
                    {deliveryDaySection.heading}
                  </h2>
                  {deliveryDaySection.paragraphs.map((paragraph, idx) => (
                    <p
                      key={idx}
                      className="text-sm md:text-base lg:text-lg font-bold text-primary"
                    >
                      {paragraph}
                    </p>
                  ))}
                  <p className="text-sm md:text-base lg:text-lg text-primary">
                    When they drop off your new car, they’ll take time to show
                    you how everything works, explain any technical bits and
                    check everything’s in order.
                  </p>
                  <p className="text-sm md:text-base lg:text-lg text-primary">
                    When it’s all signed for, they’ll hand over the keys.
                    Simple!
                  </p>
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-brandAccent pt-10 md:pt-0 font-bold">
                    Prefer collection?
                  </h2>
                  <p className="text-sm md:text-base lg:text-lg text-primary">
                    Meet a ZORQK handover specialist at a collection location
                    near you.
                  </p>
                </div>

                <div>
                  <img
                    src={deliveryDaySection.imageUrl}
                    alt={deliveryDaySection.heading}
                    className="w-full rounded-2xl object-cover h-[300px] md:h-[500px]"
                  />
                </div>
              </div>
              {/* 
              <Link
                to={"#"}
                className="flex w-full justify-center py-2 my-20 border-2 border-accent text-accent font-bold rounded-full hover:bg-accent hover:text-white transition"
              >
                More on collections
              </Link> */}
            </>
          )}
        </div>
      </section>

      {/* “Ready to hit the open road?” SECTION */}
      <section
        className="relative py-10 sm:py-20 -mb-32 md:-mb-60 z-10"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {isAdmin && editingSection !== "ready-open-road" && (
          <BiEdit
            onClick={() => setEditingSection("ready-open-road")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Ready to Search"
          />
        )}

        <div className="absolute inset-0 bg-[#1f2954]/90" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
          {editingSection === "ready-open-road" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="flex flex-col space-y-2">
                <input
                  type="text"
                  className="w-full mb-2 px-3 py-2 rounded bg-primary text-brandAccent"
                  value={readyOpenRoadSection.preamble}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "ready-open-road"
                          ? { ...sec, preamble: e.target.value }
                          : sec
                      ),
                    }))
                  }
                  placeholder="Preamble (e.g. Let’s find your next car)"
                />
                <input
                  type="text"
                  className="w-full mb-2 px-3 py-2 rounded bg-primary text-secondary"
                  value={readyOpenRoadSection.heading}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "ready-open-road"
                          ? { ...sec, heading: e.target.value }
                          : sec
                      ),
                    }))
                  }
                  placeholder="Heading (e.g. Ready to hit the open road?)"
                />
                <input
                  type="text"
                  className="w-full mb-2 px-3 py-2 rounded bg-primary text-white"
                  value={readyOpenRoadSection.button.text}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "ready-open-road"
                          ? {
                              ...sec,
                              button: { ...sec.button, text: e.target.value },
                            }
                          : sec
                      ),
                    }))
                  }
                  placeholder="Button Text (e.g. Search all cars)"
                />
                <input
                  type="text"
                  className="w-full mb-2 px-3 py-2 rounded bg-primary text-white"
                  value={readyOpenRoadSection.button.href}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "ready-open-road"
                          ? {
                              ...sec,
                              button: { ...sec.button, href: e.target.value },
                            }
                          : sec
                      ),
                    }))
                  }
                  placeholder="Button Link (e.g. /used-cars)"
                />
                <div className="flex space-x-2">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={() => saveContent({ ...pageContent })}
                  >
                    Save
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded"
                    onClick={() => setEditingSection(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <BiSearch className="text-4xl text-brandAccent" />
                <div>
                  <p className="text-lg text-brandAccent">
                    {readyOpenRoadSection.preamble}
                  </p>
                  <h2 className="text-2xl md:text-3xl font-bold text-secondary">
                    {readyOpenRoadSection.heading}
                  </h2>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-20 pb-20 sm:pb-40">
              <div className="mb-6 sm:mb-0 text-center sm:text-left">
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  {readyOpenRoadSection.heading}
                </h2>
              </div>
              <Link
                to={readyOpenRoadSection.button.href}
                className="inline-block text-lg border-2 border-white bg-transparent hover:bg-white hover:text-accent text-white font-semibold px-6 md:px-24 py-2 rounded-full transition"
              >
                {readyOpenRoadSection.button.text}
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// // src/pages/Delivery.jsx

// import React, { useEffect, useState, useRef } from "react";
// import { toast } from "react-toastify";
// import { BiEdit, BiSearch } from "react-icons/bi";
// import { Link, useNavigate } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../api/axios";
// import PageImageSelector from "../components/ui/PageImageSelector";
// import LoadingSpinner from "../components/LoadingSpinner";
// import heroImg from "../assets/car-delivery/car-delivery-hero-bg.jpg";
// import { HiMiniCheckBadge } from "react-icons/hi2";
// import guaranteeIcon from "../assets/findcar/guarantee_icon.png";
// import ratingIcon from "../assets/findcar/rating_icon.png";
// import del1 from "../assets/car-delivery/home-delivery.png";
// import mg1 from "../assets/car-delivery/Mask group1.png";
// import mg2 from "../assets/car-delivery/Maskgroup2.png";
// import mg3 from "../assets/car-delivery/Maskgroup 3.png";
// import bgImage from "../assets/car-delivery/bgimage.png";

// export default function Delivery() {
//   const { user } = useAuth();
//   const isAdmin = user?.roles.includes("super-admin");
//   const navigate = useNavigate();

//   const [pageContent, setPageContent] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   // Tracks which “section key” is currently in edit mode
//   const [editingSection, setEditingSection] = useState(null);

//   // Refs for hidden file inputs
//   const heroFileInputRef = useRef(null);
//   const behindScenesFileInputRef = useRef(null);
//   const deliveryDayFileInputRef = useRef(null);
//   const preferCollectionFileInputRef = useRef(null);

//   useEffect(() => {
//     api
//       .get("/pages/delivery")
//       .then((res) => {
//         setPageContent(res.data);
//         setLoading(false);
//       })
//       .catch((err) => {
//         console.error(err);
//         setError("Cannot load page content");
//         setLoading(false);
//       });
//   }, []);

//   if (loading) return <LoadingSpinner />;
//   if (error) return <p className="text-red-600">{error}</p>;
//   if (!pageContent) return <p>No content</p>;

//   async function saveContent(updatedContent) {
//     try {
//       await api.put("/pages/delivery", { content: updatedContent });
//       setPageContent(updatedContent);
//       toast.success("Saved successfully");
//       setEditingSection(null);
//     } catch (e) {
//       console.error(e);
//       toast.error("Failed to save");
//     }
//   }

//   async function uploadPageImage(file, onUrlReceived) {
//     const form = new FormData();
//     form.append("pageKey", "delivery");
//     form.append("images", file);

//     try {
//       const res = await api.post("/page-images", form, {
//         headers: { "Content-Type": "multipart/form-data" },
//       });
//       const newImg = res.data[0];
//       onUrlReceived(newImg.url);
//     } catch (err) {
//       console.error("Upload failed:", err);
//       toast.error("Image upload failed");
//     }
//   }

//   // ────────────────────────────────────────────────────────────────────────────
//   // SECTION DATA EXTRACTION (keys must match backend JSON exactly)
//   // ────────────────────────────────────────────────────────────────────────────

//   // 1) HERO
//   const hero = pageContent.hero || { title: "", subtitle: "", imageUrl: "" };

//   // 2) “Where do we deliver?”
//   const whereDeliverSection = pageContent.sections.find(
//     (s) => s.key === "where-do-we-deliver"
//   ) || {
//     key: "where-do-we-deliver",
//     heading: "Where do we deliver?",
//     paragraphs: [],
//   };

//   // 3) “Behind the scenes”
//   const behindScenesSection = pageContent.sections.find(
//     (s) => s.key === "behind-the-scenes"
//   ) || {
//     key: "behind-the-scenes",
//     heading: "Behind the scenes",
//     paragraphs: [],
//     imageUrl: "",
//   };

//   // 4) “The delivery day”
//   const deliveryDaySection = pageContent.sections.find(
//     (s) => s.key === "delivery-day"
//   ) || {
//     key: "delivery-day",
//     heading: "The delivery day",
//     paragraphs: [],
//     imageUrl: "",
//   };

//   // 5) “Prefer collection?”
//   const preferCollectionSection = pageContent.sections.find(
//     (s) => s.key === "prefer-collection"
//   ) || {
//     key: "prefer-collection",
//     heading: "Prefer collection?",
//     subheading: "",
//     button: { text: "", href: "" },
//     imageUrl: "",
//   };

//   // 6) “Ready to hit the open road?”
//   const readyOpenRoadSection = pageContent.sections.find(
//     (s) => s.key === "ready-open-road"
//   ) || {
//     key: "ready-open-road",
//     preamble: "",
//     heading: "Ready to hit the open road?",
//     button: { text: "", href: "" },
//   };

//   return (
//     <div className="w-full">
//       {/* ─── HERO SECTION ────────────────────────────────────────────────────── */}
//       <section className="relative w-full h-[100vh] flex items-end text-white overflow-hidden">
//         {/* Background image */}
//         <img
//           src={heroImg || hero.imageUrl}
//           alt="Hero background"
//           className="absolute inset-0 w-full h-full object-cover z-0"
//         />

//         {/* FULL OVERLAY tint instead of gradient */}
//         <div
//           className="absolute inset-0 z-10"
//           style={{
//             background:
//               "linear-gradient(rgba(31, 41, 84, 0.5) 10%, rgba(239, 81, 75, 0.6) 77%)",
//           }}
//         />

//         {/* Text content */}
//         <div className="relative z-20 p-6 md:p-20 mb-20">
//           <h1 className="text-3xl md:text-5xl font-bold mb-4">
//             {"Welcome to ZORQK" || hero.title}
//           </h1>
//           <p className="text-2xl md:text-5xl font-bold">
//             {"The future of motoring platforms" || hero.subtitle}
//           </p>
//         </div>
//       </section>

//       {/* Delivery By Specialist Section  */}
//       <section className="relative -mt-20 bg-[#1c2553] text-white py-40 rounded-t-[50px] z-30">
//         <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 ">
//           {/* Left content  */}
//           <div className="flex flex-col justify-center">
//             <h2 className="text-accent text-3xl md:text-4xl font-bold mb-6">
//               Home Delivery
//             </h2>
//             <p className="text-lg leading-relaxed text-white/90 max-w-3xl">
//               Your car delivered, when you’re free. Just choose a day and time
//               that suits you, relax, and we’ll do the rest.
//             </p>

//             {/* <p className="text-lg leading-relaxed text-white/90 max-w-3xl">
//               Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed
//               semper velit eget volutpat auctor. Cras accumsan, neque eget
//               vestibulum iaculis, nibh magna scelerisque ipsum, vel fermentum
//               risus nisl quis ante.
//             </p> */}

//             <div className="flex flex-col justify-between items-start text-center gap-10 pt-20">
//               {/* Icon Block: Reviews */}
//               <div className="flex flex-row gap-5 justify-center items-center">
//                 <div className="w-20 h-20 md:w-28 md:h-28 bg-accent rounded-full flex items-center justify-center shadow text-white">
//                   <img
//                     src={ratingIcon}
//                     alt="5 star reviews"
//                     className="w-8 h-8 md:w-16 md:h-16 object-contain"
//                   />
//                 </div>
//                 <p className="font-bold text-lg text-center">5* Reviews</p>
//               </div>

//               {/* Satisfaction icon */}
//               <div className="flex flex-row gap-5 justify-center items-center">
//                 <div className="w-20 h-20 md:w-28 md:h-28 bg-accent rounded-full flex items-center justify-center shadow text-white">
//                   <img
//                     src={guaranteeIcon}
//                     alt="14 day guarantee"
//                     className="w-8 h-8 md:w-16 md:h-16 object-cover"
//                   />
//                 </div>
//                 <p className="font-bold text-lg text-center">
//                   Satisfaction Guaranteed
//                 </p>
//               </div>
//             </div>
//           </div>

//           {/* Right image  */}
//           <div>
//             <img
//               src={del1} // Replace with drivesSection.imageUrl if dynamic
//               alt="What drives us"
//               className="w-full h-[100vh] rounded-3xl object-cover"
//             />
//           </div>
//         </div>
//       </section>

//       {/* Where do we deliver? */}
//       <section className="max-w-6xl mx-auto flex flex-col gap-20 justify-center items-center py-32">
//         {/* content  */}
//         <div className="flex flex-col gap-10 justify-center items-center px-20 text-center">
//           <h1 className="text-4xl text-accent font-bold">
//             Where do we deliver?
//           </h1>
//           <p className="text-primary font-bold">
//             We offer home delivery to mainland Great Britain and the Isle of
//             Wight postcodes. Pick a delivery slot, Monday to Saturday, and we’ll
//             deliver your car to your door.
//           </p>
//           <p className="text-primary">
//             We don’t deliver to Northern Ireland, the Scottish Islands, the
//             Isles of Scilly, the Isle of Man, Jersey, Guernsey and the other
//             Channel Islands.{" "}
//           </p>
//           <p className="text-primary">
//             We also reserve the right not to deliver to certain addresses due to
//             vehicle access limitations.
//           </p>
//           <button className="px-20 py-2 border border-accent text-accent font-bold rounded-full hover:bg-accent hover:text-white transition">
//             Find out more
//           </button>
//         </div>
//         {/* images  */}
//         <div className="flex gap-20 w-full h-[40vh] max-w-4xl mx-auto">
//           <img className="object-cover" src={mg1} alt="mask group 1" />
//           <img
//             className="object-cover w-full h-full"
//             src={mg2}
//             alt="mask group 2"
//           />
//           <img
//             className="object-cover w-full h-full"
//             src={mg3}
//             alt="mask group 3"
//           />
//         </div>
//       </section>

//       {/* ─── “Behind the scenes” SECTION ──────────────────────────────────────── */}
//       <section className="bg-accent py-0 md:py-32 relative">
//         {isAdmin && editingSection !== "behind-the-scenes" && (
//           <BiEdit
//             onClick={() => setEditingSection("behind-the-scenes")}
//             className="absolute top-4 right-4 text-xl text-white cursor-pointer hover:text-accent"
//             title="Edit Behind the scenes"
//           />
//         )}
//         <div className="max-w-6xl mx-auto">
//           {editingSection === "behind-the-scenes" ? (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
//               <div className="flex justify-center">
//                 <img
//                   src={behindScenesSection.imageUrl}
//                   alt={behindScenesSection.heading}
//                   className="w-full rounded-2xl object-contain"
//                 />
//               </div>

//               <div className="space-y-4">
//                 <input
//                   type="text"
//                   className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
//                   value={behindScenesSection.heading}
//                   onChange={(e) =>
//                     setPageContent((prev) => ({
//                       ...prev,
//                       sections: prev.sections.map((sec) =>
//                         sec.key === "behind-the-scenes"
//                           ? { ...sec, heading: e.target.value }
//                           : sec
//                       ),
//                     }))
//                   }
//                   placeholder="Section Heading"
//                 />
//                 {behindScenesSection.paragraphs.map((para, idx) => (
//                   <textarea
//                     key={idx}
//                     className="text-primary w-full mb-2 px-3 py-2 rounded"
//                     rows={3}
//                     value={behindScenesSection.paragraphs[idx]}
//                     onChange={(e) => {
//                       const newParas = [...behindScenesSection.paragraphs];
//                       newParas[idx] = e.target.value;
//                       setPageContent((prev) => ({
//                         ...prev,
//                         sections: prev.sections.map((sec) =>
//                           sec.key === "behind-the-scenes"
//                             ? { ...sec, paragraphs: newParas }
//                             : sec
//                         ),
//                       }));
//                     }}
//                     placeholder={`Paragraph ${idx + 1}`}
//                   />
//                 ))}

//                 <div className="mb-2">
//                   <label className="block mb-1 text-sm font-medium text-primary">
//                     Section Image
//                   </label>
//                   <PageImageSelector
//                     pageKey="delivery"
//                     value={behindScenesSection.imageUrl}
//                     onChange={(newUrl) => {
//                       if (newUrl === "__upload_new__") {
//                         behindScenesFileInputRef.current.click();
//                       } else {
//                         setPageContent((prev) => ({
//                           ...prev,
//                           sections: prev.sections.map((sec) =>
//                             sec.key === "behind-the-scenes"
//                               ? { ...sec, imageUrl: newUrl }
//                               : sec
//                           ),
//                         }));
//                       }
//                     }}
//                   />
//                   <input
//                     type="file"
//                     accept="image/*"
//                     ref={behindScenesFileInputRef}
//                     className="hidden"
//                     onChange={(e) => {
//                       const file = e.target.files[0];
//                       if (!file) return;
//                       uploadPageImage(file, (url) => {
//                         setPageContent((prev) => ({
//                           ...prev,
//                           sections: prev.sections.map((sec) =>
//                             sec.key === "behind-the-scenes"
//                               ? { ...sec, imageUrl: url }
//                               : sec
//                           ),
//                         }));
//                       });
//                       e.target.value = null;
//                     }}
//                   />
//                 </div>

//                 <div className="flex space-x-2">
//                   <button
//                     className="px-4 py-2 bg-green-600 text-white rounded"
//                     onClick={() => saveContent({ ...pageContent })}
//                   >
//                     Save
//                   </button>
//                   <button
//                     className="px-4 py-2 bg-gray-600 text-white rounded"
//                     onClick={() => setEditingSection(null)}
//                   >
//                     Cancel
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ) : (
//             <div className="grid grid-cols-1 md:[grid-template-columns:55%_40%] gap-16 items-center justify-center">
//               <div className="flex justify-center">
//                 <img
//                   src={behindScenesSection.imageUrl}
//                   alt={behindScenesSection.heading}
//                   className="w-full rounded-2xl object-contain"
//                 />
//               </div>
//               <div className="space-y-5 flex flex-wrap">
//                 <h2 className="text-white text-3xl md:text-4xl font-bold mb-12">
//                   {behindScenesSection.heading}
//                 </h2>
//                 <p className="font-bold">
//                   When you click ‘buy’ our experts kick into gear.
//                 </p>
//                 {behindScenesSection.paragraphs.map((paragraph, idx) => (
//                   <p key={idx} className="text-white pb-12">
//                     {paragraph}
//                   </p>
//                 ))}
//                 <button
//                   onClick={() => navigate("/used-cars")}
//                   className="px-32 py-2 border-2 border-white text-white font-bold rounded-full hover:bg-accent hover:text-white transition"
//                 >
//                   Find your dream car
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>
//       </section>

//       {/* ─── “The delivery day” SECTION ────────────────────────────────────────── */}
//       <section className="bg-white py-16 relative">
//         {isAdmin && editingSection !== "delivery-day" && (
//           <BiEdit
//             onClick={() => setEditingSection("delivery-day")}
//             className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
//             title="Edit The delivery day"
//           />
//         )}
//         <div className="max-w-5xl mx-auto">
//           {editingSection === "delivery-day" ? (
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
//               <div className="space-y-4">
//                 <input
//                   type="text"
//                   className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
//                   value={deliveryDaySection.heading}
//                   onChange={(e) =>
//                     setPageContent((prev) => ({
//                       ...prev,
//                       sections: prev.sections.map((sec) =>
//                         sec.key === "delivery-day"
//                           ? { ...sec, heading: e.target.value }
//                           : sec
//                       ),
//                     }))
//                   }
//                   placeholder="Section Heading"
//                 />
//                 {deliveryDaySection.paragraphs.map((para, idx) => (
//                   <textarea
//                     key={idx}
//                     className="text-primary w-full mb-2 px-3 py-2 rounded"
//                     rows={3}
//                     value={deliveryDaySection.paragraphs[idx]}
//                     onChange={(e) => {
//                       const newParas = [...deliveryDaySection.paragraphs];
//                       newParas[idx] = e.target.value;
//                       setPageContent((prev) => ({
//                         ...prev,
//                         sections: prev.sections.map((sec) =>
//                           sec.key === "delivery-day"
//                             ? { ...sec, paragraphs: newParas }
//                             : sec
//                         ),
//                       }));
//                     }}
//                     placeholder={`Paragraph ${idx + 1}`}
//                   />
//                 ))}

//                 <div className="mb-2">
//                   <label className="block mb-1 text-sm font-medium text-primary">
//                     Section Image
//                   </label>
//                   <PageImageSelector
//                     pageKey="delivery"
//                     value={deliveryDaySection.imageUrl}
//                     onChange={(newUrl) => {
//                       if (newUrl === "__upload_new__") {
//                         deliveryDayFileInputRef.current.click();
//                       } else {
//                         setPageContent((prev) => ({
//                           ...prev,
//                           sections: prev.sections.map((sec) =>
//                             sec.key === "delivery-day"
//                               ? { ...sec, imageUrl: newUrl }
//                               : sec
//                           ),
//                         }));
//                       }
//                     }}
//                   />
//                   <input
//                     type="file"
//                     accept="image/*"
//                     ref={deliveryDayFileInputRef}
//                     className="hidden"
//                     onChange={(e) => {
//                       const file = e.target.files[0];
//                       if (!file) return;
//                       uploadPageImage(file, (url) => {
//                         setPageContent((prev) => ({
//                           ...prev,
//                           sections: prev.sections.map((sec) =>
//                             sec.key === "delivery-day"
//                               ? { ...sec, imageUrl: url }
//                               : sec
//                           ),
//                         }));
//                       });
//                       e.target.value = null;
//                     }}
//                   />
//                 </div>

//                 <div className="flex space-x-2">
//                   <button
//                     className="px-4 py-2 bg-green-600 text-white rounded"
//                     onClick={() => saveContent({ ...pageContent })}
//                   >
//                     Save
//                   </button>
//                   <button
//                     className="px-4 py-2 bg-gray-600 text-white rounded"
//                     onClick={() => setEditingSection(null)}
//                   >
//                     Cancel
//                   </button>
//                 </div>
//               </div>

//               <div className="flex">
//                 <img
//                   src={deliveryDaySection.imageUrl}
//                   alt={deliveryDaySection.heading}
//                   className="w-full rounded-2xl object-cover h-[500px]"
//                 />
//               </div>
//             </div>
//           ) : (
//             <>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
//                 <div className="space-y-8">
//                   <h2 className="text-brandAccent text-3xl md:text-4xl font-bold">
//                     {deliveryDaySection.heading}
//                   </h2>
//                   {deliveryDaySection.paragraphs.map((paragraph, idx) => (
//                     <p key={idx} className="font-bold text-primary">
//                       {paragraph}
//                     </p>
//                   ))}
//                   <p className="text-primary">
//                     When they drop off your new car, they’ll take time to show
//                     you how everything works, explain any technical bits and
//                     check everything’s in order.{" "}
//                   </p>
//                   <p className="text-primary">
//                     When it’s all signed for, they’ll hand over the keys.
//                     Simple!
//                   </p>
//                   <h2 className="text-brandAccent text-3xl md:text-4xl font-bold">
//                     Perfer collection?
//                   </h2>
//                   <p className="text-primary">
//                     Meet a ZORQK handover specialist at a collection location
//                     near you.
//                   </p>
//                 </div>

//                 <div className="flex">
//                   <img
//                     src={deliveryDaySection.imageUrl}
//                     alt={deliveryDaySection.heading}
//                     className="w-full rounded-2xl object-cover h-[500px]"
//                   />
//                 </div>
//               </div>

//               <Link
//                 to={"#"}
//                 className="flex justify-center px-16 py-2 my-20 border-2 border-accent text-accent font-bold rounded-full hover:bg-accent hover:text-white transition"
//               >
//                 More on collections
//               </Link>
//             </>
//           )}
//         </div>
//       </section>

//       {/* ─── “Ready to hit the open road?” SECTION ─────────────────────────────────── */}
//       <section
//         className="relative py-20 -mb-60 z-10"
//         style={{
//           backgroundImage: `url(${bgImage})`,
//           backgroundSize: "cover",
//           backgroundPosition: "center",
//         }}
//       >
//         {isAdmin && editingSection !== "ready-open-road" && (
//           <BiEdit
//             onClick={() => setEditingSection("ready-open-road")}
//             className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
//             title="Edit Ready to Search"
//           />
//         )}

//         <div className="absolute inset-0 bg-[#1f2954]/90" />

//         <div className="relative z-10 max-w-5xl mx-auto px-4">
//           {editingSection === "ready-open-road" ? (
//             <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-6">
//               <div className="flex flex-col space-y-2">
//                 <input
//                   type="text"
//                   className="text-brandAccent w-full mb-2 px-3 py-2 rounded bg-primary"
//                   value={readyOpenRoadSection.preamble}
//                   onChange={(e) =>
//                     setPageContent((prev) => ({
//                       ...prev,
//                       sections: prev.sections.map((sec) =>
//                         sec.key === "ready-open-road"
//                           ? { ...sec, preamble: e.target.value }
//                           : sec
//                       ),
//                     }))
//                   }
//                   placeholder="Preamble (e.g. Let’s find your next car)"
//                 />
//                 <input
//                   type="text"
//                   className="text-secondary w-full mb-2 px-3 py-2 rounded bg-primary"
//                   value={readyOpenRoadSection.heading}
//                   onChange={(e) =>
//                     setPageContent((prev) => ({
//                       ...prev,
//                       sections: prev.sections.map((sec) =>
//                         sec.key === "ready-open-road"
//                           ? { ...sec, heading: e.target.value }
//                           : sec
//                       ),
//                     }))
//                   }
//                   placeholder="Heading (e.g. Ready to hit the open road?)"
//                 />
//                 <input
//                   type="text"
//                   className="text-white w-full mb-2 px-3 py-2 rounded bg-primary"
//                   value={readyOpenRoadSection.button.text}
//                   onChange={(e) =>
//                     setPageContent((prev) => ({
//                       ...prev,
//                       sections: prev.sections.map((sec) =>
//                         sec.key === "ready-open-road"
//                           ? {
//                               ...sec,
//                               button: { ...sec.button, text: e.target.value },
//                             }
//                           : sec
//                       ),
//                     }))
//                   }
//                   placeholder="Button Text (e.g. Search all cars)"
//                 />
//                 <input
//                   type="text"
//                   className="text-white w-full mb-2 px-3 py-2 rounded bg-primary"
//                   value={readyOpenRoadSection.button.href}
//                   onChange={(e) =>
//                     setPageContent((prev) => ({
//                       ...prev,
//                       sections: prev.sections.map((sec) =>
//                         sec.key === "ready-open-road"
//                           ? {
//                               ...sec,
//                               button: { ...sec.button, href: e.target.value },
//                             }
//                           : sec
//                       ),
//                     }))
//                   }
//                   placeholder="Button Link (e.g. /used-cars)"
//                 />
//                 <div className="flex space-x-2">
//                   <button
//                     className="px-4 py-2 bg-green-600 text-white rounded"
//                     onClick={() => saveContent({ ...pageContent })}
//                   >
//                     Save
//                   </button>
//                   <button
//                     className="px-4 py-2 bg-gray-600 text-white rounded"
//                     onClick={() => setEditingSection(null)}
//                   >
//                     Cancel
//                   </button>
//                 </div>
//               </div>

//               <div className="flex items-center space-x-3">
//                 <BiSearch className="text-4xl text-brandAccent" />
//                 <div>
//                   <p className="text-lg text-brandAccent">
//                     {readyOpenRoadSection.preamble}
//                   </p>
//                   <h2 className="text-2xl md:text-3xl font-bold text-secondary">
//                     {readyOpenRoadSection.heading}
//                   </h2>
//                 </div>
//               </div>
//             </div>
//           ) : (
//             <div className="flex items-center justify-center gap-20 pb-40">
//               {/* Left column: icon + texts */}
//               <div>
//                 {/* <BiSearch className="text-4xl text-brandAccent" /> */}
//                 <div>
//                   {/* <p className="text-lg text-brandAccent">
//                     {readyOpenRoadSection.preamble}
//                   </p> */}
//                   <h2 className="text-2xl md:text-3xl font-bold text-white">
//                     {readyOpenRoadSection.heading}
//                   </h2>
//                 </div>
//               </div>

//               {/* Right column: centered “Search all cars” button */}
//               <div>
//                 <Link
//                   to={readyOpenRoadSection.button.href}
//                   className="
//                     text-lg
//                     inline-block
//                     border-2
//                     border-white
//                     bg-transparent hover:bg-white hover:text-accent text-white
//                     font-semibold
//                     px-24 py-2
//                     rounded-full
//                     transition
//                   "
//                 >
//                   {readyOpenRoadSection.button.text}
//                 </Link>
//               </div>
//             </div>
//           )}
//         </div>
//       </section>
//     </div>
//   );
// }
